import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Loader2, User, Award, Mail, Phone, MapPin, Quote, Upload, Image } from "lucide-react";

const AboutManager = () => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    coach_name: "",
    title: "",
    quote: "",
    bio: "",
    image_url: "",
    years_coaching: 15,
    d1_athletes: 50,
    certifications: "",
    email: "",
    phone: "",
    location: ""
  });

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get(`${API}/about`);
      const data = response.data;
      setFormData({
        coach_name: data.coach_name || "",
        title: data.title || "",
        quote: data.quote || "",
        bio: Array.isArray(data.bio) ? data.bio.join('\n\n') : data.bio || "",
        image_url: data.image_url || "",
        years_coaching: data.years_coaching || 15,
        d1_athletes: data.d1_athletes || 50,
        certifications: Array.isArray(data.certifications) ? data.certifications.join(', ') : data.certifications || "",
        email: data.email || "",
        phone: data.phone || "",
        location: data.location || ""
      });
    } catch (e) {
      toast.error("Failed to fetch about content");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'about');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Construct full URL
      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      setFormData({ ...formData, image_url: fullUrl });
      toast.success("Photo uploaded!");
    } catch (e) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        coach_name: formData.coach_name,
        title: formData.title,
        quote: formData.quote,
        bio: formData.bio.split('\n\n').filter(p => p.trim()),
        image_url: formData.image_url,
        years_coaching: parseInt(formData.years_coaching),
        d1_athletes: parseInt(formData.d1_athletes),
        certifications: formData.certifications.split(',').map(c => c.trim()).filter(c => c),
        email: formData.email,
        phone: formData.phone,
        location: formData.location
      };
      
      await authAxios.put('/about', payload);
      toast.success("About section updated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-pink animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <User className="w-5 h-5 text-neon-pink" />
          About Me Section
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo Upload Section */}
        <div className="cyber-card p-6 border-neon-pink/30">
          <h3 className="font-orbitron text-sm text-neon-pink mb-4 flex items-center gap-2">
            <Upload className="w-4 h-4" />
            COACH PHOTO
          </h3>
          
          <div className="flex items-start gap-6">
            {/* Preview */}
            <div className="w-32 h-40 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
              {formData.image_url ? (
                <img src={formData.image_url} alt="Coach Preview" className="w-full h-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-gray-600" />
              )}
            </div>

            <div className="flex-1 space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                id="coach-photo-upload"
              />
              <label
                htmlFor="coach-photo-upload"
                className={`cyber-btn cyber-btn-pink py-3 px-6 text-sm cursor-pointer inline-flex items-center gap-2 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    UPLOADING...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    UPLOAD PHOTO
                  </>
                )}
              </label>
              <p className="font-rajdhani text-xs text-gray-500">
                Recommended: Portrait orientation, at least 400x500px
              </p>
              <div className="space-y-1">
                <Label className="font-orbitron text-xs text-gray-500">Or paste image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://..."
                  className="bg-void-black border-white/10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="cyber-card p-6 space-y-4">
          <h3 className="font-orbitron text-sm text-neon-blue mb-4 flex items-center gap-2">
            <User className="w-4 h-4" />
            COACH INFORMATION
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Coach Name</Label>
              <Input
                value={formData.coach_name}
                onChange={(e) => setFormData({...formData, coach_name: e.target.value})}
                placeholder="Coach Mike"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="HEAD PERFORMANCE COACH"
                className="bg-void-black border-white/10"
              />
            </div>
          </div>
        </div>

        {/* Quote */}
        <div className="cyber-card p-6 space-y-4">
          <h3 className="font-orbitron text-sm text-neon-pink mb-4 flex items-center gap-2">
            <Quote className="w-4 h-4" />
            INSPIRATIONAL QUOTE
          </h3>
          
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Quote</Label>
            <Input
              value={formData.quote}
              onChange={(e) => setFormData({...formData, quote: e.target.value})}
              placeholder="Champions are made when nobody's watching."
              className="bg-void-black border-white/10"
            />
          </div>
        </div>

        {/* Bio */}
        <div className="cyber-card p-6 space-y-4">
          <h3 className="font-orbitron text-sm text-neon-blue mb-4">BIO / STORY</h3>
          
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">
              Bio (separate paragraphs with blank lines)
            </Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              placeholder="Your story and background..."
              rows={8}
              className="bg-void-black border-white/10 font-rajdhani"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="cyber-card p-6 space-y-4">
          <h3 className="font-orbitron text-sm text-neon-green mb-4 flex items-center gap-2">
            <Award className="w-4 h-4" />
            CREDENTIALS & STATS
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Years Coaching</Label>
              <Input
                type="number"
                value={formData.years_coaching}
                onChange={(e) => setFormData({...formData, years_coaching: e.target.value})}
                placeholder="15"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">D1 Athletes Trained</Label>
              <Input
                type="number"
                value={formData.d1_athletes}
                onChange={(e) => setFormData({...formData, d1_athletes: e.target.value})}
                placeholder="50"
                className="bg-void-black border-white/10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">
              Certifications (comma separated)
            </Label>
            <Input
              value={formData.certifications}
              onChange={(e) => setFormData({...formData, certifications: e.target.value})}
              placeholder="CSCS, NASM-CPT, USA Weightlifting L2"
              className="bg-void-black border-white/10"
            />
          </div>
        </div>

        {/* Contact */}
        <div className="cyber-card p-6 space-y-4">
          <h3 className="font-orbitron text-sm text-cyan-400 mb-4 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            CONTACT INFORMATION
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400 flex items-center gap-1">
                <Mail className="w-3 h-3" /> Email
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="coach@grindtime.com"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400 flex items-center gap-1">
                <Phone className="w-3 h-3" /> Phone
              </Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(555) 123-4567"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Location
              </Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="Los Angeles, CA"
                className="bg-void-black border-white/10"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="cyber-btn cyber-btn-pink py-3 px-8 text-sm disabled:opacity-50"
          >
            <span className="flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              SAVE CHANGES
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutManager;
